import { route } from 'preact-router';
import { useAuth } from '../context/AuthContext.js';
import { rememberReturnPath } from '../services/signInReturnPath.js';

/**
 * 080: the second — and last — place a visitor's intent is captured on the way to sign-in.
 *
 * `AuthGuard` (079 D1) covers the visitor who was never signed in. This covers the visitor whose
 * session is rejected *while they are already on a screen*: `fetchState.ts:47` turns a 401 into
 * `status: 'unauthenticated'`, `ResultState` puts a **Sign in** button under it, and eight screens
 * wire that button.
 *
 * 🔴 Capture alone does not fix it, and that is why this is a hook rather than a plain function.
 * `AuthProvider` asks `/users/me` **once, on mount** (`useEffect(…, [])`), so after a mid-use 401
 * `isAuthenticated` is still `true` — stale. `LoginPage`'s own effect fires on mount when
 * `isAuthenticated` is true and routes straight back out to the resolved destination. Sending a
 * still-"authenticated" visitor to the sign-in screen therefore bounces them off it: today to the
 * default (the F9 symptom), and — once the destination is remembered — back to the screen that just
 * rejected them, forever. Measured all three ways before this was written (research R2).
 *
 * So the dead session is ENDED first. `logout()` is reached through the context because it is the
 * only thing that flips `isAuthenticated` in React state; clearing `localStorage` by hand leaves
 * the state true and the bounce intact — a fix that looks right in the diff and fails in a browser.
 *
 * 🔴 This file is NOT `signInReturnPath.ts`, deliberately. That module is filtered out of the
 * caller walk in `AuthGuard.test.tsx`, so a capture helper placed there would be invisible to the
 * assertion guarding which files may capture — the guard would stay green while the property it
 * protects quietly stopped holding (research R5).
 */
export function useSignInRedirect(): () => void {
  const { logout } = useAuth();

  return () => {
    // Before the navigation, matching `AuthGuard:29`: `route()` is synchronous, so a later write
    // would race the sign-in screen's own mount. Read at call time, so a screen whose address
    // changed after mounting captures the address the visitor is actually looking at.
    rememberReturnPath(window.location.pathname + window.location.search);

    // 🔴 Before `route()`, never after. The order is forced in both directions: navigate first and
    // the sign-in screen mounts still-authenticated and bounces the visitor onward. `logout()` does
    // not navigate and does not touch sessionStorage, so the destination just captured survives it,
    // and its `/auth/logout` POST is fire-and-forget — correct for a session already rejected.
    logout();

    route('/account/login');
  };
}
